/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import * as actions from "../../actions";
import ActionBar from "components/action-bar/action-bar";
import Menu from "components/menu/menu";
import get from "lodash/get";
import { PUBLISHER_MEDIA_CATEGORIES, EXTERNAL_MEDIA_PROVIDERS } from "../../media-list";
import { t } from "i18n";
import { ThunkDispatch } from "redux-thunk";
import { PartialAppState, Providers, CurrentProvider } from "pages/media-library/state";

export interface MediaActionBarProps {
  switchProvider: (item: CurrentProvider) => void;
  currentMediaProvider: CurrentProvider;
  providers: Providers;
  isBannerPresent: boolean;
  isDesktopSizeViewport: boolean;
  enableGettyImagesInSidebar: boolean;
}

const getMediaCounts = (providers: Providers) => {
  return {
    getty: get(providers, ["getty", "page", "total"], 0),
    "media-library": get(providers, ["media-library", "page", "total"], 0)
  };
};

const MediaActionBar = ({
  switchProvider,
  currentMediaProvider,
  providers,
  isBannerPresent,
  isDesktopSizeViewport,
  enableGettyImagesInSidebar
}: MediaActionBarProps) => {
  return (
    <ActionBar
      isBannerPresent={isBannerPresent}
      isDesktopSizeViewport={isDesktopSizeViewport}
      renderContents={(onItemClickCallback) => (
        <React.Fragment>
          <Menu
            title={t("mediaLibrary.library")}
            items={PUBLISHER_MEDIA_CATEGORIES(t)}
            onItemClick={(item) => {
              switchProvider(item);
              onItemClickCallback();
            }}
            getItemLabel={(item) => item.label}
            isItemSelected={(item) => item.value === currentMediaProvider.value}
            badgeValue={getMediaCounts(providers)["media-library"]}
          />
          {enableGettyImagesInSidebar && (
            <Menu
              title={t("mediaLibrary.others")}
              items={EXTERNAL_MEDIA_PROVIDERS(t)}
              onItemClick={(item) => {
                switchProvider(item);
                onItemClickCallback();
              }}
              getItemLabel={(item) => item.label}
              isItemSelected={(item) => item.value === currentMediaProvider.value}
              badgeValue={getMediaCounts(providers)["getty"]}
            />
          )}
        </React.Fragment>
      )}
    />
  );
};

const mapStateToProps = (state: PartialAppState) => {
  return {
    currentMediaProvider: state.mediaLibrary.currentMediaProvider,
    providers: state.mediaLibrary.providers,
    enableGettyImagesInSidebar: state.features.enableGettyImagesInSidebar
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    switchProvider: (provider: CurrentProvider) =>
      dispatch({ type: actions.MEDIA_LIBRARY_SET_PROVIDER, payload: { provider } })
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(MediaActionBar);

export { MediaActionBar };
